import { useEffect, useRef, useState } from "preact/hooks";
import { track } from "../host/telemetry.js";
import { FULL_AD, SKIP_AFTER_S, adNetworkForMe } from "./ads.js";
import { appInterstitial, inApp, webAdBreak } from "./network.js";

/**
 * The full-screen ad (ADR 0026), from the network where there is one: AdMob's interstitial in the
 * app, AdSense's ad break on the web once the H5 Games beta is approved. Otherwise, or if the
 * network fails, Obean's own (below).
 */
export function FullAd(props: { gameId: string; onDone: () => void }) {
  const net = adNetworkForMe.value;
  const [house, setHouse] = useState(false);
  const unit = inApp() ? net?.admob?.interstitial : undefined;
  const h5 = !inApp() && net?.adsense?.h5 === true;
  useEffect(() => {
    if (house || !net || (!unit && !h5)) return;
    const shown = () => {
      track("ad_shown", {
        slot: "full",
        where: "solo",
        source: "network",
        ad_id: "network",
        game_id: props.gameId,
      });
    };
    if (unit)
      appInterstitial(net, unit).then(
        () => {
          shown();
          props.onDone();
        },
        () => {
          setHouse(true);
        },
      );
    else
      webAdBreak("solo-retry", (viewed) => {
        if (viewed) shown();
        props.onDone();
      });
  }, [house]);
  if (house || !net || (!unit && !h5)) return <HouseFullAd {...props} />;
  // The network's ad covers the screen; a quiet page underneath meanwhile.
  return <section class="full-ad" role="dialog" aria-label="Advertisement" aria-modal="true" />;
}

/**
 * The full-screen ad (ADR 0026), solo only: after three losses in a row on a mission, before the
 * next try. Skippable after 5 seconds; it offers the Pass in plain words, no pressure.
 */
function HouseFullAd(props: { gameId: string; onDone: () => void }) {
  const [left, setLeft] = useState(SKIP_AFTER_S);
  const shownAt = useRef(performance.now());
  useEffect(() => {
    track("ad_shown", {
      slot: "full",
      where: "solo",
      source: "house",
      ad_id: FULL_AD.id,
      game_id: props.gameId,
    });
    const t = setInterval(() => {
      setLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => {
      clearInterval(t);
    };
  }, []);
  const skip = () => {
    track("ad_dismissed", {
      where: "solo",
      after_s: Math.round((performance.now() - shownAt.current) / 1000),
      ad_id: FULL_AD.id,
    });
    props.onDone();
  };
  return (
    <section class="full-ad" role="dialog" aria-label="Advertisement" aria-modal="true">
      <div class="full-ad-card">
        <span class="ad-tag">Ad</span>
        <h2>Obean Pass</h2>
        <p>No ads, for you and everyone in rooms you host. Host as often as you like.</p>
        <a
          class="secondary"
          href={FULL_AD.href}
          target="_blank"
          rel="noopener"
          onClick={() => {
            track("ad_cta", { slot: "full", where: "solo", ad_id: FULL_AD.id });
          }}
        >
          {FULL_AD.cta}
        </a>
        <button type="button" disabled={left > 0} onClick={skip}>
          {left > 0 ? `Skip in ${String(left)}` : "Skip ad"}
        </button>
      </div>
    </section>
  );
}
